import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { getUsers, addUser, updateUser } from "../api/userApi";
import { showError } from "../utils/alert";

const initialForm = {
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  username: "",
  password: "",
  status: "0",
};

export default function UserForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadUser();
    }
  }, [id]);

  const loadUser = async () => {
    try {
      const res = await getUsers({ page: 1, per_page: 1000 });
      const user = res.data.items.find((u) => u.id === parseInt(id));
      if (user) {
        setForm({
          first_name: user.first_name || "",
          last_name: user.last_name || "",
          email: user.email || "",
          mobile: user.mobile || "",
          username: user.username || "",
          password: "",
          status: user.status === "1" ? "1" : "0",
        });
      } else {
        showError("User not found");
        navigate("/users");
      }
    } catch (err) {
      showError("Failed to load user");
      navigate("/users");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      return showError("First name is required");
    }
    if (!form.email.trim()) {
      return showError("Email is required");
    }
    if (!isEdit && !form.password) {
      return showError("Password is required for new user");
    }

    setLoading(true);
    try {
      const payload = { ...form };
      if (isEdit && !payload.password) {
        delete payload.password;
      }
      if (isEdit) {
        await updateUser(id, payload);
      } else {
        await addUser(payload);
      }
      navigate("/users");
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      if (!isEdit && err?.response?.status === 409) {
        showError(apiMessage || "Account already exists");
      } else {
        showError(apiMessage || (isEdit ? "Failed to update user" : "Failed to add user"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title={isEdit ? "Edit User" : "Add User"}>
      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">
            {isEdit ? "Edit User" : "Add New User"}
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">First name *</label>
              <input
                name="first_name"
                className="form-control"
                value={form.first_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Last name</label>
              <input
                name="last_name"
                className="form-control"
                value={form.last_name}
                onChange={handleChange}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Email *</label>
              <input
                name="email"
                type="email"
                className="form-control"
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Mobile</label>
              <input
                name="mobile"
                className="form-control"
                value={form.mobile}
                onChange={handleChange}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                name="status"
                className="form-select"
                value={form.status}
                onChange={handleChange}
              >
                <option value="0">Inactive</option>
                <option value="1">Active</option>
              </select>
            </div>
            
            <div className="mb-3">
              <label className="form-label">
                Password {isEdit && <span className="text-muted">(leave blank to keep)</span>}
              </label>
              <input
                name="password"
                type="password"
                className="form-control"
                value={form.password}
                onChange={handleChange}
                required={!isEdit}
              />
            </div>
            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn skedq-accent-btn"
                disabled={loading}
              >
                {loading ? "Saving..." : isEdit ? "Update" : "Add"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate("/users")}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}
